import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import z from 'zod';

import WithdrawToast from '@/components/toast/withdraw-toast';
import { getBalanceQueryKey } from '@/features/account/api/get-balance';
import { getTransactionsQueryKey } from '@/features/account/api/get-transactions';
import { queryClient } from '@/lib/api-clients/query-client';
import { privateV2Api } from '@/lib/api-clients/rest-client';
import { useAccountStore } from '@/store/use-account-store';
import { ErrorCode, EventType, getErrorMsg } from '@/types';
import { getUSDCAddress } from '@/utils/addresses';
import { sendEvent } from '@/utils/analytics';
import { bigIntToDecimalStr, bigIntToNum } from '@/utils/value-format';

import { getChainDecimals } from '../wagmi/config';

const createWithdrawInputSchema = z.object({
  size: z.bigint(),
  chainId: z.number(),
  execute: z.boolean(),
  recipient: z.string().optional(),
});

type CreateWithdrawParams = z.infer<typeof createWithdrawInputSchema>;

interface ResponseData {
  status: string; // NEW
}

export default function useCreateV2Withdraw() {
  const { t } = useTranslation();
  return useMutation<ResponseData, ErrorCode, CreateWithdrawParams>({
    mutationKey: ['/withdraw'],
    mutationFn: (data) => {
      const loadingId = toast.loading('Requesting withdrawal...');
      const nonce = useAccountStore.getState().nextNonce();
      const promise = privateV2Api.post('/v2/withdraw', {
        amount: bigIntToDecimalStr(data.size, getChainDecimals(data.chainId)),
        chain_id: data.chainId,
        execute: data.execute,
        nonce,
        to_address: data.recipient,
        token: getUSDCAddress(data.chainId),
      });
      promise.finally(() => toast.dismiss(loadingId));
      return promise;
    },
    onSuccess: (_, { size, chainId }) => {
      toast.success(WithdrawToast);
      sendEvent(EventType.WITHDRAW_INITIATED, {
        amount: bigIntToNum(size, getChainDecimals(chainId)),
      });
      const { address, getAuthParams } = useAccountStore.getState();
      if (!address) return;
      const apiKey = getAuthParams(address, { skipFallback: true })?.apiKey;
      if (!apiKey) return;
      queryClient.refetchQueries({ queryKey: getBalanceQueryKey(apiKey) });
      queryClient.refetchQueries({ queryKey: getTransactionsQueryKey(apiKey) });
      // TODO: refetch transfers
    },
    onError: (code) => toast.error(getErrorMsg(code, t)),
  });
}
